import { useMemo } from 'react';
import { calculateOverallCompletion, OverallCompletionData } from '../utils/completionCalculations';
import { Document } from '../types/Document';

// Composition layer for company document derived data (completion stats, enhanced categories, etc.).
// Similar to useDocumentData but for company documents

interface UseCompanyDocumentDataParams {
  categories: any[];
  subcategories: any[];
  documentTypes: any[];
  documentFields: any[];
  companyDocuments: Document[];
  companyFieldValues: { [key: string]: any };
}

interface CategoryWithStats {
  _id: string;
  stats: {
    totalItems: number;
    completedItems: number;
    missingItems: number;
    expiredItems: number;
    completionRate: number;
    subcategories: any;
  };
  [key: string]: any;
}

interface UseCompanyDocumentDataResult {
  completionStats: OverallCompletionData | null;
  categoriesWithStats: CategoryWithStats[];
  overallStats: {
    totalItems: number;
    completedItems: number;
    missingItems: number;
    expiredItems: number;
    completionRate: number;
  };
}

export const useCompanyDocumentData = ({
  categories,
  subcategories,
  documentTypes,
  documentFields,
  companyDocuments,
  companyFieldValues
}: UseCompanyDocumentDataParams): UseCompanyDocumentDataResult => {
  // Memoize completion stats
  const completionStats = useMemo(() => {
    if (!categories.length || !subcategories.length) {
      return {
        categories: {},
        subcategories: {},
        overall: {
          totalItems: 0,
          completedItems: 0,
          missingItems: 0,
          expiredItems: 0,
          completionRate: 0
        }
      };
    }
    
    return calculateOverallCompletion(
      categories,
      subcategories,
      documentTypes,
      documentFields,
      companyDocuments,
      companyFieldValues
    );
  }, [
    categories,
    subcategories,
    documentTypes,
    documentFields,
    companyDocuments,
    companyFieldValues
  ]);

  // Enhanced categories with stats
  const categoriesWithStats = useMemo((): CategoryWithStats[] => {
    if (!completionStats || !categories.length) return [];
    
    return categories.map((category: any): CategoryWithStats => ({
      ...category,
      stats: (completionStats.categories as any)[category.id] || {
        totalItems: 0,
        completedItems: 0,
        missingItems: 0,
        expiredItems: 0,
        completionRate: 0,
        subcategories: {}
      }
    }));
  }, [categories, completionStats]);

  // Overall stats
  const overallStats = useMemo(() => {
    if (!completionStats) {
      return {
        totalItems: 0,
        completedItems: 0,
        missingItems: 0,
        expiredItems: 0,
        completionRate: 0
      };
    }
    
    return completionStats.overall;
  }, [completionStats]);

  return useMemo(() => ({
    completionStats,
    categoriesWithStats,
    overallStats
  }), [completionStats, categoriesWithStats, overallStats]);
};
